/**
 * Pulse channel wrapper.
 *
 * Loads the pulse AudioWorklet module and exposes a thin API for note
 * on/off + frequency/duty/volume control. DSP lives in dsp/pulse.ts so
 * it can be Vitest-tested without an AudioContext.
 */

export type PulseDuty = 0 | 1 | 2 | 3;

/** Duty cycle index → fraction of the wave period spent high. */
const DUTY_VALUES: Record<PulseDuty, number> = {
  0: 0.125, // 12.5%
  1: 0.25, // 25% (classic MM lead)
  2: 0.5, // 50%
  3: 0.75, // 75%
};

let workletLoaded = false;

/** Idempotently load the pulse worklet into the given AudioContext. */
export async function loadPulseWorklet(ctx: AudioContext): Promise<void> {
  if (workletLoaded) return;
  await ctx.audioWorklet.addModule('/worklets/pulse.js');
  workletLoaded = true;
}

export interface PulseNode {
  setFrequency(hz: number): void;
  setDuty(duty: PulseDuty): void;
  setVolume(volume: number): void;
  /**
   * Start (or re-trigger) the note. Pass `attackMs > 0` for a re-attack
   * envelope that lets back-to-back notes of the same pitch read as two
   * distinct hits. `attackMs = 0` (default) plays sustained.
   */
  noteOn(attackMs?: number): void;
  noteOff(): void;
  destroy(): void;
}

/**
 * Create a PulseNode wired to ctx.destination.
 * The pulse worklet module must already be loaded — call
 * loadPulseWorklet(ctx) first.
 */
export function createPulseNode(ctx: AudioContext): PulseNode {
  const node = new AudioWorkletNode(ctx, 'pulse-processor', {
    numberOfInputs: 0,
    numberOfOutputs: 1,
    outputChannelCount: [1],
  });
  node.connect(ctx.destination);

  const set = (name: 'frequency' | 'duty' | 'volume', value: number) => {
    const p = node.parameters.get(name);
    if (p) p.value = value;
  };

  return {
    setFrequency: (hz) => set('frequency', hz),
    setDuty: (duty) => set('duty', DUTY_VALUES[duty]),
    setVolume: (vol) => set('volume', vol),
    noteOn: (attackMs = 0) => node.port.postMessage({ type: 'noteOn', attackMs }),
    noteOff: () => node.port.postMessage({ type: 'noteOff' }),
    destroy: () => {
      node.port.postMessage({ type: 'noteOff' });
      node.disconnect();
    },
  };
}

/**
 * Convert scientific pitch notation to Hz (12-TET, A4 = 440).
 * Accepts "C-5", "C#5", "Bb3", "C5" — the optional `-` is just a separator.
 *
 * Lives here for now; will move to its own module when the period table
 * (NES exact-pitch) lands alongside the scheduler.
 */
export function noteToFrequency(note: string): number {
  const match = /^([A-G])([#b])?-?(\d)$/.exec(note);
  if (!match) throw new Error(`Invalid note: ${note}`);
  const letter = match[1]!;
  const accidental = match[2];
  const octave = parseInt(match[3]!, 10);

  const semitones: Record<string, number> = {
    C: -9,
    D: -7,
    E: -5,
    F: -4,
    G: -2,
    A: 0,
    B: 2,
  };
  const accOffset = accidental === '#' ? 1 : accidental === 'b' ? -1 : 0;
  const semitonesFromA4 = (octave - 4) * 12 + semitones[letter]! + accOffset;
  return 440 * Math.pow(2, semitonesFromA4 / 12);
}
